import SearchableSelect from "./SearchableSelect";

// Common Austrian + DACH cities. User can also type free-text — values that aren't in
// this list will still be accepted via the "free text" fallback at the bottom.
const CITIES_BY_COUNTRY = {
  AT: [
    "Wien", "Graz", "Linz", "Salzburg", "Innsbruck", "Klagenfurt", "Villach",
    "Wels", "Sankt Pölten", "Dornbirn", "Steyr", "Wiener Neustadt", "Feldkirch",
    "Bregenz", "Leonding", "Klosterneuburg", "Baden", "Wolfsberg", "Leoben",
    "Krems an der Donau", "Traun", "Amstetten", "Lustenau", "Kapfenberg",
    "Mödling", "Hallein", "Kufstein", "Traiskirchen", "Schwechat", "Braunau am Inn",
    "Stockerau", "Saalfelden", "Ansfelden", "Tulln", "Hohenems", "Spittal an der Drau",
    "Telfs", "Ternitz", "Perchtoldsdorf", "Bad Ischl", "Eisenstadt", "Gmunden",
    "Vöcklabruck", "Bludenz", "Korneuburg", "Hall in Tirol", "Ried im Innkreis",
    "Bischofshofen", "Schwaz", "Lienz", "Bruck an der Mur",
  ],
  DE: [
    "Berlin", "Hamburg", "München", "Köln", "Frankfurt am Main", "Stuttgart",
    "Düsseldorf", "Leipzig", "Dortmund", "Essen", "Bremen", "Dresden", "Hannover",
    "Nürnberg", "Duisburg", "Bochum", "Wuppertal", "Bielefeld", "Bonn", "Münster",
    "Karlsruhe", "Mannheim", "Augsburg", "Wiesbaden", "Mönchengladbach", "Aachen",
    "Braunschweig", "Kiel", "Chemnitz", "Halle (Saale)", "Magdeburg", "Freiburg im Breisgau",
    "Krefeld", "Lübeck", "Mainz", "Erfurt", "Oberhausen", "Rostock", "Kassel",
    "Hagen", "Saarbrücken", "Hamm", "Mülheim an der Ruhr", "Potsdam", "Ludwigshafen am Rhein",
    "Oldenburg", "Leverkusen", "Osnabrück", "Solingen", "Heidelberg", "Herne",
    "Neuss", "Darmstadt", "Paderborn", "Regensburg", "Ingolstadt", "Würzburg",
    "Fürth", "Wolfsburg", "Offenbach am Main", "Ulm", "Heilbronn", "Pforzheim",
    "Göttingen", "Bottrop", "Trier", "Recklinghausen", "Reutlingen", "Bremerhaven",
    "Koblenz", "Bergisch Gladbach", "Jena", "Remscheid", "Erlangen", "Moers",
    "Siegen", "Hildesheim", "Salzgitter", "Passau",
  ],
  CH: [
    "Zürich", "Genf", "Basel", "Lausanne", "Bern", "Winterthur", "Luzern",
    "St. Gallen", "Lugano", "Biel/Bienne", "Thun", "Köniz", "La Chaux-de-Fonds",
    "Schaffhausen", "Freiburg", "Chur", "Vernier", "Neuenburg", "Uster", "Sitten",
    "Zug", "Yverdon-les-Bains", "Aarau", "Solothurn", "Frauenfeld",
  ],
  LI: ["Vaduz", "Schaan", "Triesen", "Balzers", "Eschen", "Mauren"],
  IT: [
    "Rom", "Mailand", "Neapel", "Turin", "Palermo", "Genua", "Bologna", "Florenz",
    "Bari", "Catania", "Venedig", "Verona", "Messina", "Padua", "Triest", "Brescia",
    "Tarent", "Prato", "Reggio Calabria", "Modena", "Parma", "Bozen", "Meran",
    "Bruneck", "Sterzing",
  ],
  HU: ["Budapest", "Debrecen", "Miskolc", "Szeged", "Pécs", "Győr", "Sopron", "Eisenstadt"],
  CZ: ["Prag", "Brünn", "Ostrava", "Pilsen", "Liberec", "Olmütz", "Budweis"],
  SK: ["Bratislava", "Košice", "Prešov", "Žilina", "Banská Bystrica", "Nitra"],
  SI: ["Ljubljana", "Maribor", "Celje", "Kranj", "Koper", "Velenje"],
  HR: ["Zagreb", "Split", "Rijeka", "Osijek", "Zadar", "Pula", "Slavonski Brod"],
  PL: ["Warschau", "Krakau", "Łódź", "Breslau", "Posen", "Danzig", "Stettin", "Lublin", "Bromberg", "Kattowitz"],
};

export default function CityPicker({
  value,
  onChange,
  country = "AT",
  placeholder = "— Stadt wählen oder eingeben —",
  testid = "city-picker",
  disabled = false,
  allowFreeText = true,
}) {
  const list = CITIES_BY_COUNTRY[country] || CITIES_BY_COUNTRY.AT;
  const set = new Set(list);
  // If current value is a free-text city not in the list, include it so it stays selected
  if (value && !set.has(value)) list.unshift(value);
  const options = list.map((c) => ({ value: c, label: c }));

  if (allowFreeText) {
    // Hybrid: searchable dropdown but user can also type a city not in the list — we expose
    // a small text input via the SearchableSelect's search box: if no match, "Erstellen"-Option.
    return (
      <SearchableSelectWithCreate
        options={options}
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        testid={testid}
        disabled={disabled}
      />
    );
  }

  return (
    <SearchableSelect
      options={options}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      testid={testid}
      disabled={disabled}
      emptyText="Stadt nicht gefunden"
    />
  );
}

import { useEffect, useMemo, useRef, useState } from "react";
import { Search, ChevronDown, Plus, X } from "lucide-react";

function SearchableSelectWithCreate({ options, value, onChange, placeholder, testid, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find((o) => o.value === value) || (value ? { value, label: value } : null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => (o.label || "").toLowerCase().includes(q));
  }, [options, query]);

  const exactMatch = filtered.some((o) => (o.label || "").toLowerCase() === query.trim().toLowerCase());
  const showCreate = query.trim() && !exactMatch;

  useEffect(() => {
    function onDoc(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    }
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const baseInput =
    "w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:ring-2 focus:ring-blue-600 focus:outline-none disabled:opacity-50";

  function pick(v) {
    onChange?.(v);
    setOpen(false);
    setQuery("");
  }

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`${baseInput} flex items-center justify-between gap-2 text-left`}
      >
        <span className={`truncate ${selected ? "text-zinc-100" : "text-zinc-500"}`}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="flex items-center gap-1 text-zinc-500">
          {selected && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); pick(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); pick(""); } }}
              className="hover:text-zinc-200 p-0.5"
              aria-label="Auswahl löschen"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-zinc-900 border border-zinc-800 rounded shadow-2xl">
          <div className="p-2 border-b border-zinc-800 flex items-center gap-2">
            <Search className="w-3.5 h-3.5 text-zinc-500" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Suchen oder neuen Ort eingeben…"
              className="bg-transparent text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none flex-1"
              data-testid={testid ? `${testid}-search` : undefined}
              onKeyDown={(e) => {
                if (e.key === "Enter" && showCreate) {
                  e.preventDefault();
                  pick(query.trim());
                }
              }}
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => pick(o.value)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-zinc-800 ${
                  o.value === value ? "bg-blue-600/10 text-blue-300" : "text-zinc-100"
                }`}
              >
                {o.label}
              </button>
            ))}
            {showCreate && (
              <button
                type="button"
                onClick={() => pick(query.trim())}
                className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-800 text-emerald-400 flex items-center gap-2"
                data-testid={testid ? `${testid}-create` : undefined}
              >
                <Plus className="w-3.5 h-3.5" /> „{query.trim()}" verwenden
              </button>
            )}
            {!showCreate && filtered.length === 0 && (
              <div className="px-3 py-3 text-xs text-zinc-500 italic">Keine Treffer</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
